/** Angular */
import { Component, Input, Output, EventEmitter, HostBinding } from '@angular/core';
import { Router } from '@angular/router';
import { FormControl, Validators } from '@angular/forms';
import { trigger, state, style, transition, animate } from '@angular/animations';
/** end angular */

/** Sixense */
import { BydAuthService } from '@sixense/byd-auth-service';
/** end sixense */

/** Models */
import { DsLibMainMenuLink } from './models/ds-lib-main-menu-link.model';
import { DsLibMainMenuTab } from './models/ds-lib-main-menu-tab.model';
import { DsMainMenuLogos } from './models/ds-main-menu-logos.model';
/** end models */

/** Interfaces */
import { DsLibMainMenuSectionInterface } from './interfaces/ds-lib-main-menu-section.interface';
import { DsLibMainMenuUserInterface } from './interfaces/ds-lib-main-menu-user.interface';
/** end interfaces */

@Component({
  selector: 'ds-lib-main-menu',
  templateUrl: 'ds-lib-main-menu.component.html',
  styleUrls: ['ds-lib-main-menu.component.scss'],
  animations: [
    // rotation of arrow icon on section open/close
    trigger('expandedArrow', [
      state('default', style({ transform: 'rotate(0)' })),
      state('rotated', style({ transform: 'rotate(-180deg)' })),
      transition('rotated => default', animate('200ms ease-out')),
      transition('default => rotated', animate('200ms ease-in')),
    ]),
    // used to block initial render animation of children elements.
    trigger('blockInitialRenderAnimation', [transition(':enter', [])]),
    // animation of opening/closing section links
    trigger('expandedSection', [
      transition(':enter', [
        // :enter is alias to 'void => *'
        style({ height: '0' }),
        animate(200, style({ height: '*' })),
      ]),
      transition(':leave', [
        // :leave is alias to '* => void'
        animate(200, style({ height: '0' })),
      ]),
    ]),
  ],
})

/**
 * Menu component
 */
export class DsLibMainMenuComponent {
  /** Product logs urls */
  @Input() productLogos: DsMainMenuLogos = {};

  /** Lits of tabs containing all links for the menu */
  @Input() tabs: DsLibMainMenuTab[] = [];

  /**
   * Font size of title elements. CSS styling.
   * Main font size can be changed directly via parent component's css, and setting font-size property.
   */
  @Input() titleFontSize = 'large';

  /** Redirection url when clicking on user profile bar. */
  @Input() accountInfoUrl = 'account/infos';

  /**
   *
   */
  @Input() user: DsLibMainMenuUserInterface = null;

  /**
   * List of all user rights
   */
  @Input() rights: number[] = [];

  /** Selected tab id (double-way binding) */
  @Input() selectedTabId: number;

  /**
   *
   */
  @Output() selectedTabIdChange = new EventEmitter<number>();

  /** Event emitted when the home link is clicked */
  @Output() homeClicked = new EventEmitter<DsLibMainMenuLink>();

  /** Event emitted when a tab is clicked */
  @Output() tabClicked = new EventEmitter<DsLibMainMenuTab>();

  /** Event emitted when an item is selected inside a select dropdown */
  @Output() selectItemClicked = new EventEmitter<DsLibMainMenuTab>();

  /** Event emitted when a link is clicked inside a section */
  @Output() linkClicked = new EventEmitter<DsLibMainMenuLink>();

  /**
   * Form control of the selected project
   */
  public projectSelectForm = new FormControl([Validators.required]);

  /**
   * Is menu expanded.
   */
  @HostBinding('class.expanded') isExpanded: boolean;

  /**
   * Creates an instance of DsLibMainMenuComponent.
   *
   * @param router NgModule that provides navigation
   * @param bydAuthService the byd authentication service
   */
  constructor(private router: Router, private bydAuthService: BydAuthService) {
    this.isExpanded = false;
  }

  /**
   * Selects tab and emits it to the parent component.
   *
   * @param tab Tab to select
   */
  selectTab(tab: DsLibMainMenuTab) {
    this.closeAllSelects(this.tabs);
    this.selectedTabId = tab.id;
    this.selectedTabIdChange.emit(this.selectedTabId);
  }

  /**
   * Select an item inside select dropdown and emit it.
   *
   * @param eventTab the tab event
   */
  // TODO Set the correct type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSelectItemClicked(eventTab: any) {
    eventTab.tab.selectedItem = eventTab.item;
    this.closeSelect(eventTab.tab);
    this.selectItemClicked.emit(eventTab.tab);
  }

  /**
   * Expands the menu.
   */
  public expandMenu() {
    this.isExpanded = true;
  }

  /**
   * Toggles the menu (opened/closed).
   */
  public toggleMenuExpand() {
    this.isExpanded = !this.isExpanded;
  }

  /**
   * Close select dropdown of selected tab.
   *
   * @param tab DsLibMainMenuTab
   */
  closeSelect(tab: DsLibMainMenuTab) {
    tab.selectExpanded = false;
  }

  /**
   * Close all tabs select dropdowns.
   *
   * @param tabs DsLibMainMenuTab
   */
  closeAllSelects(tabs: DsLibMainMenuTab[]) {
    tabs.forEach((tab) => this.closeSelect(tab));
  }

  /**
   * Emits clicked tab to parent component.
   *
   * @param tab DsLibMainMenuTab
   */
  onTabClicked(tab: DsLibMainMenuTab) {
    this.tabClicked.emit(tab);
  }

  /**
   * Emits clicked link to home page to parent component.
   *
   * @param home Clicked home link
   */
  onHomeClicked(home: DsLibMainMenuLink) {
    this.homeClicked.emit(home);
  }

  /**
   * Toggle expanded section.
   *
   * @param section the clicked section
   */
  public onSectionClicked(section: DsLibMainMenuSectionInterface) {
    this.toggleExpandedSection(section);
    if (!section.link) {
      this.expandMenu();
    }
  }

  /**
   * Logout from roazhon, and redirect user to login page.
   */
  onLogout() {
    this.bydAuthService.logout();
    this.router.navigate(['login']);
  }

  /**
   * Toggle a section between the expanded/closed states.
   *
   * @param section Selected section
   */
  private toggleExpandedSection(section: DsLibMainMenuSectionInterface) {
    if (!section.expandable) {
      return;
    }
    section.expanded = !section.expanded;
  }
}
